/**
 * When may the OPTIONAL "Also kick to FOH" on Stop / End Paused Work run?
 *
 * The checkbox asks to hand the WHOLE ticket to the front of house. That is
 * only true to the facts when this technician's act ended the last live BOH
 * work on the ticket. A Stop that failed, found nothing to stop, or left a
 * colleague running or paused does not end BOH work, so the requested handoff
 * must not happen — the ticket stays with the shop.
 *
 * The idle fact comes from the server (`ticket_became_idle`), computed under
 * the ticket transition lock in the same transaction as the Stop. It is never
 * re-derived from a projection read after commit: that read can race another
 * technician's Start, and two technicians stopping together serialize on the
 * ticket row, so at most one of them is told the ticket became idle.
 *
 * This does NOT make the handoff automatic. It only decides whether a handoff
 * the technician explicitly asked for may execute.
 */

/** What the timer act established — only a committed boundary counts. */
export type StopBoundaryResult =
  | { succeeded: true; ticketBecameIdle: boolean }
  | { succeeded: false };

export type StopFohHandoffDecision =
  /** Nothing was requested, or the timer act did not succeed. */
  | 'none'
  /** Requested, the act succeeded and ended the last live BOH work. */
  | 'kick'
  /** Requested and the act succeeded, but other BOH work is still live. */
  | 'suppressed_other_work_live';

export function decideStopFohHandoff(
  requested: boolean,
  boundary: StopBoundaryResult,
): StopFohHandoffDecision {
  if (!requested || !boundary.succeeded) return 'none';
  return boundary.ticketBecameIdle ? 'kick' : 'suppressed_other_work_live';
}

/**
 * STAGE 0B — the same rule for the optional "Also kick to FOH" on PAUSE.
 *
 * A pause is self-scoped. The whole ticket may be handed to FOH only when
 * this pause is the ONLY live BOH work left: no colleague still running AND
 * no colleague's unresolved pause (a paused colleague still owns BOH work).
 * That fact is `pause_left_no_other_live_work`, counted by `pause_my_timer`
 * under the ticket transition lock — never inferred from a post-commit read.
 */
export type PauseBoundaryResult =
  | { succeeded: true; pauseLeftNoOtherLiveWork: boolean }
  | { succeeded: false };

export function decidePauseFohHandoff(
  requested: boolean,
  boundary: PauseBoundaryResult,
): StopFohHandoffDecision {
  if (!requested || !boundary.succeeded) return 'none';
  return boundary.pauseLeftNoOtherLiveWork ? 'kick' : 'suppressed_other_work_live';
}
